import { ExclamationTriangleIcon, UserIcon } from '@heroicons/react/24/outline'
import { formatThreatLevel } from '../../utils/amlResultCategories'
import { isWatchlistAmlProvider, sanitizeVendorBranding } from '../../utils/sanitizeVendorBranding'

/**
 * Subject-level watchlist screening context (who was screened + exposure that matches result tabs).
 */
export default function GreenIdScreeningSummary({ summary, compact = false }) {
  if (!summary || !isWatchlistAmlProvider(summary.provider)) return null

  const subject = summary.subject || {}
  const counts = summary.exposureCounts || {}
  const pepCount = counts.pepCategoryRows ?? counts.pepList ?? 0
  const adverseCount = counts.adverseMedia ?? 0
  const sanctionsCount = counts.sanctionsCategoryRows ?? counts.sanctionsList ?? 0
  const associatesCount = counts.associates ?? 0
  const narrative = summary.narrative ? sanitizeVendorBranding(summary.narrative) : null

  const badge = (yes, label, count) => (
    <span
      className={`px-2.5 py-1 rounded-md border font-medium ${
        yes
          ? label.startsWith('PEP')
            ? 'bg-red-100 border-red-300 text-red-900'
            : label.startsWith('Adverse')
              ? 'bg-amber-100 border-amber-300 text-amber-900'
              : 'bg-purple-100 border-purple-300 text-purple-900'
          : 'bg-gray-100 border-gray-300 text-gray-700'
      }`}
    >
      {label}: {yes ? 'Yes' : 'No'}
      {yes && count > 0 ? ` (${count})` : ''}
    </span>
  )

  return (
    <div
      className={`border rounded-lg ${
        summary.needsReview
          ? 'border-amber-300 bg-amber-50'
          : 'border-gray-200 bg-gray-50'
      } ${compact ? 'p-3 mb-3' : 'p-4 mb-4'}`}
    >
      <div className="flex items-start gap-3">
        {summary.needsReview && (
          <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          <h4 className={`font-semibold text-gray-900 ${compact ? 'text-sm' : 'text-base'}`}>
            Screened subject
          </h4>
          <p className={`text-gray-900 font-medium ${compact ? 'text-sm' : 'text-lg'}`}>
            <UserIcon className="inline h-4 w-4 mr-1 text-gray-500" />
            {subject.fullName || '—'}
            {subject.country && (
              <span className="text-gray-600 font-normal text-sm ml-2">({subject.country})</span>
            )}
          </p>

          <div className="mt-3 flex flex-wrap gap-2 text-sm">
            {badge(summary.pepStatus === 'yes', 'PEP', pepCount)}
            {badge(summary.adverseMediaStatus === 'yes', 'Adverse media', adverseCount)}
            {badge(summary.sanctionsStatus === 'yes', 'Sanctions', sanctionsCount)}
            {associatesCount > 0 && (
              <span className="px-2.5 py-1 rounded-md border font-medium bg-blue-50 border-blue-200 text-blue-900">
                Associates ({associatesCount})
              </span>
            )}
          </div>

          <dl className={`mt-2 grid gap-x-4 gap-y-1 text-sm ${compact ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {summary.verificationId && (
              <>
                <dt className="text-gray-600">Screening reference</dt>
                <dd className="font-mono text-gray-900">{summary.verificationId}</dd>
              </>
            )}
            {summary.overallStatus && (
              <>
                <dt className="text-gray-600">Overall status</dt>
                <dd className="font-medium text-gray-900">
                  {formatThreatLevel(summary.overallStatus)}
                </dd>
              </>
            )}
            {counts.totalFieldHits != null && (
              <>
                <dt className="text-gray-600">Rows in results</dt>
                <dd className="font-medium text-gray-900">{counts.totalFieldHits}</dd>
              </>
            )}
          </dl>

          {summary.needsReview && (
            <div className="mt-3 px-3 py-2 rounded-md bg-amber-100 border border-amber-200 text-sm text-amber-900">
              <strong>Review required</strong>
              {summary.overallStatus && (
                <span> — overall status: {formatThreatLevel(summary.overallStatus)}</span>
              )}
              .
            </div>
          )}

          {narrative && !compact && (
            <p className="mt-3 text-sm text-gray-700 leading-relaxed border-t border-amber-200/80 pt-3">
              {narrative}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
